import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';

export function AboutPage() {
  return (
    <article className="max-w-3xl mx-auto px-4 pt-12 pb-20 sm:pt-16 aces-prose">
      <SEO
        title="About ACES — Independent commerce learning for Indian Plus One & Plus Two"
        description="ACES is an independent learning platform built around the CBSE NCERT and Kerala SCERT Higher Secondary commerce syllabi. We are not affiliated with NCERT, CBSE, or the Government of Kerala."
        canonical="https://acescommerce.com/about/"
      />
      <h1>About ACES</h1>
      <p>
        ACES is a free, independent learning platform for students of
        Accountancy, Business Studies, and Economics in Class 11 and Class 12 across India. Our content is
        structured around the public syllabi of CBSE (NCERT-aligned) and Kerala&rsquo;s Higher Secondary curriculum,
        which the Directorate of Higher Secondary Education (DHSE) runs, with textbooks from SCERT Kerala.
      </p>
      <p>
        We exist for one reason: a 16-year-old commerce student in India should be able to feel a journal entry
        post itself, watch a ratio collapse when a decision goes wrong, and ask &ldquo;why&rdquo; without waiting
        for a 1:60 classroom teacher. We build interactive simulations, worked examples, practice exercises, and
        graduated hints — every one of which is original content, written for the student in front of the
        screen.
      </p>

      <h2>What we are not</h2>
      <ul>
        <li>
          <strong>Not affiliated with NCERT, CBSE, or the Government of Kerala.</strong> Their syllabi are
          public curriculum specifications and any school can teach to them; we do that independently.
        </li>
        <li>
          <strong>Not a textbook reproduction.</strong> Our chapter narratives are original. We do not
          republish NCERT prose, exercises, or solutions verbatim.
        </li>
        <li>
          <strong>Not a coaching institute.</strong> No fees, no tutor placement, no exam guarantees.
        </li>
      </ul>

      <h2>How we keep content honest</h2>
      <p>
        Chapter drafts are generated with AI assistance and then pass through separate verification steps. The
        full process is on our <Link to="/sources/" className="text-accent-600 underline">Sources page</Link>.
        If a chapter is still being re-checked, it carries an &ldquo;Under review&rdquo; badge so you can see its status.
      </p>

      <h2>How we are funded</h2>
      <p>
        ACES is free for students. We plan to run unobtrusive ads (Google AdSense) once our application is approved, to help cover
        infrastructure costs. We do not sell student data; see our{' '}
        <Link to="/privacy/" className="text-accent-600 underline">Privacy Policy</Link> for details.
      </p>

      <h2>Who&rsquo;s behind ACES</h2>
      <div className="aces-card flex flex-col sm:flex-row gap-5 items-start not-prose my-6 p-5 sm:p-6">
        <img
          src="/brand/koshy-thomas.webp"
          alt="Koshy Thomas — Plus One &amp; Plus Two Commerce teacher"
          width={80}
          height={80}
          className="rounded-full shrink-0 shadow-card ring-2 ring-accent-500/70 ring-offset-2 ring-offset-cream"
        />
        <div className="text-sm text-ink-700 leading-relaxed">
          <p className="font-semibold text-ink-900 mb-1">Koshy Thomas</p>
          <p>
            <strong>Koshy Thomas</strong> is the public face of ACES. A Plus One &amp; Plus Two Commerce teacher
            based in Kerala with over a decade in the classroom, Koshy Thomas runs the YouTube
            channel,{' '}
            <a
              href="https://www.youtube.com/channel/UCh4KBkzwSbk-TEWZTi1maxw"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-600 underline"
            >
              Koshy Thomas Online Classes
            </a>
            , has grown to <strong>14,000+ subscribers</strong> — students from across Kerala and beyond who
            found that a calm, patient explanation beats a crowded tuition hall. ACES is a companion
            to those classes, with interactive simulations and self-paced exercises layered on top.
          </p>
        </div>
      </div>

      <h2>Get in touch</h2>
      <p>
        Errors, missing topics, or a chapter that wasn&rsquo;t clear?{' '}
        <Link to="/contact/" className="text-accent-600 underline">Tell us</Link>. Every correction makes the
        next student&rsquo;s experience better.
      </p>
    </article>
  );
}
